import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FinalStates,
  PaymentMethodRestriction,
  ResultMessageTypes,
} from '@cashier/cashier-model';
import { AmountConverter } from '@cashier/cashier-shared';
import { PlainHtmlContainer } from '@cashier/cashier-ui';

import { useStatusBlock } from '../useStatusBlock';

interface StatusBlockProps {
  amount: string;
  currency: string;
  paymentMethodMessage: string | null;
  onButtonClick: () => void;
}

export const StatusBlock = ({
  amount,
  currency,
  paymentMethodMessage,
  onButtonClick,
}: StatusBlockProps): ReactElement => {
  const { t } = useTranslation();
  const { finalState, message } = useStatusBlock();

  const statusInfoMessageMap: Record<FinalStates, string> = {
    [FinalStates.ERROR]: t('failedTransaction.message'),
    [FinalStates.PAYMENT_METHOD_ERROR]: t('failedTransaction.message'),
    [FinalStates.PENDING]: t('pendingTransaction.message'),
    [FinalStates.SUCCESS]: t('successfulTransaction.message'),
    [FinalStates.RESTRICTED]: message?.content || '',
  };

  return (
    <div className="final-screen final-screen--visible">
      <p
        className={`final-screen__${finalState.toString()}-message`}
        data-test="final-screen-message"
      >
        {statusInfoMessageMap[finalState]}
      </p>
      <div
        className={`final-screen__icon--${finalState.toString()}`}
        data-test="final-screen-icon"
      />
      {amount && (
        <h3 className="final-screen__sum" data-test="final-screen-amount">
          {AmountConverter.exponentialNotationToNumber(
            String(AmountConverter.toDelimitedNumber(amount || 0)),
          )}{' '}
          {currency}
        </h3>
      )}
      {finalState === FinalStates.ERROR && message && (
        <div className="final-screen__error-box" data-test="error-box">
          <p className="final-screen__error-box-title" data-test="error-title">
            {t('errorSubmitting.heading')}
          </p>
          {message.type === ResultMessageTypes.STRING && (
            <p
              className="final-screen__error-box-message"
              data-test="error-message"
            >
              {message.content}
            </p>
          )}
          {message.type === ResultMessageTypes.HTML && (
            <PlainHtmlContainer
              data-test="error-message"
              className={'final-screen__error-box-message'}
              content={message.content}
              config={{
                ADD_ATTR: ['target'],
              }}
            />
          )}
        </div>
      )}
      {finalState === FinalStates.PENDING && paymentMethodMessage && (
        <PlainHtmlContainer content={paymentMethodMessage} />
      )}
      <button
        data-test="new-transaction-button"
        className="final-screen__button"
        type="button"
        onClick={() => onButtonClick()}
      >
        {finalState === FinalStates.RESTRICTED
          ? t('backToSelectMethod.button')
          : t('makeNewTransaction.button')}
      </button>
    </div>
  );
};
